"use client";
import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useTransition,
} from "react";
import { Spin } from "antd";
import config from "@/config";
import Control from "./control";
import SearchPanel from "./search";
import MapService from "@/services/map";
import style from "./style.module.scss";
import StationDetails from "./location-details";
import AnaliticsHelpers from "@/helpers/analitics";
import { LoadingOutlined } from "@ant-design/icons";
import { WINDOW_BREAKPOINTS } from "@/constants/global";
import { MAP, PAGE_TABS, APP_STORE } from "@/constants";
import DownloadPopup from "@/components/download-popup";
import useQrPopupContext from "@/contexts/qr-popup-context";
import {
  IStation,
  IStationDetails,
  IMapLatLng,
  IMapBounds,
  IMapLatLngFlexible,
} from "@/interfaces/Map";
import QrPopup from "./qr-popup";
import { Nullable, QrOrigin } from "@/types";
import { Marker, GoogleMap, LoadScriptNext } from "@react-google-maps/api";
import Supercluster, { ClusterFeature, PointFeature } from "supercluster";
import {
  initialZoom,
  maxZoom,
  mob_min_zoom,
  latLngBounds,
  desc_min_zoom,
  initialCenter,
  tab_max_width,
  containerStyle,
  stationDetailsZoom,
} from "@/constants/Map";
import {
  getNavigatorPermissions,
  hasNavigatorPermissions,
} from "@/helpers/permissions-helpers";
import {
  getMarkerIcon,
  stationsToGeoJSON,
  isBoundsOutsideCached,
  addPaddingToBounds,
  getClusterGridSize,
} from "@/helpers/map-helpers";
import { GENERAL, CONFIG, UI_TEXT, IMAGE_PATHS } from "@/constants";
import { ErrorType, SuperClusterType } from "@/types";
import { toast } from "react-toastify";

const AviationMap = () => {
  const { pageViewAnalitics } = AnaliticsHelpers();
  const {
    qrPopup: contextQrPopup,
    socialType,
    closeQrPopup,
  } = useQrPopupContext();
  const { getStationById, getStationsForMap } = MapService;

  const [radius, setRadius] = useState<number>(
    CONFIG.MARKER_CLUSTERER.GRID_SIZE_FAR
  );
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [search, setSearch] = useState("");
  const [iconSize, setIconSize] = useState<number>(GENERAL.INITIAL_ICON_SIZE);
  const [qrPopup, setQrPopup] = useState(false);
  const watchIdRef = useRef<Nullable<number>>(null);
  const [isLoading, startTransition] = useTransition();
  const [isFetchingStations, setIsFetchingStations] = useState(false);
  const [minZoom, setMinZoom] = useState(desc_min_zoom);
  const [stations, setStations] = useState<IStation[]>([]);
  const [clusters, setClusters] = useState<SuperClusterType[]>([]);
  const [downloadPopup, setDownloadPopup] = useState(false);
  const [map, setMap] = useState<Nullable<google.maps.Map>>(null);
  const superclusterRef = useRef<Nullable<Supercluster>>(null);
  const [isTrackingLocation, setIsTrackingLocation] = useState(false);
  const [selectedMarker, setSelectedMarker] =
    useState<Nullable<IStation>>(null);
  const [qrOrigin, setQrOrigin] = useState<QrOrigin>(CONFIG.QR_ORIGIN.DEFAULT);
  const [mapCenter, setMapCenter] = useState<IMapLatLng>(initialCenter);
  const [userLocation, setUserLocation] = useState<Nullable<IMapLatLng>>(null);
  const [selectedStationData, setSelectedStationData] =
    useState<Nullable<IStationDetails>>(null);
  const [cachedPaddedBounds, setCachedPaddedBounds] =
    useState<Nullable<IMapBounds>>(null);
  const [mapLoadError, setMapLoadError] = useState<Nullable<string>>(null);
  const isDraggingRef = useRef(false);
  const isAnimatingRef = useRef(false);

  const stopTrackingLocation = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
      setIsTrackingLocation(false);
      setUserLocation(null);
    }
  }, []);

  const startTrackingLocation = useCallback(() => {
    if (!hasNavigatorPermissions()) {
      toast.error(UI_TEXT.ERRORS.GEOLOCATION_NOT_SUPPORTED);
      return;
    }

    if (watchIdRef.current !== null) {
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;
        const newLocation = { lat: latitude, lng: longitude };
        setUserLocation(newLocation);
      },
      (error) => {
        toast.error(UI_TEXT.ERRORS.GEOLOCATION_TRACKING_ERROR);
        if (error.code === error.PERMISSION_DENIED) {
          setIsTrackingLocation(false);
          stopTrackingLocation();
        }
      },
      {
        enableHighAccuracy: true,
        timeout: GENERAL.GEOLOCATION_TIMEOUT,
        maximumAge: 0,
      }
    );

    watchIdRef.current = watchId;
    setIsTrackingLocation(true);
  }, [stopTrackingLocation]);

  const handleCloseDownloadPopup = () => {
    setDownloadPopup(false);
  };

  const handleOpenDownloadPopupSimple = () => {
    setDownloadPopup(true);
  };

  const handleCloseQrPopup = () => {
    setQrPopup(false);
    closeQrPopup();
  };

  const getSelectedStationData = async (id: number) => {
    const res = (await getStationById(id)) as IStationDetails;
    return res;
  };

  const handleSelectStation = async (location: Nullable<IStation>) => {
    if (!location) return;

    setSelectedStationData(null);

    safeSetMapCenter(
      { lat: location.latitude, lng: location.longitude },
      stationDetailsZoom
    );

    try {
      const stationData = await getSelectedStationData(location.id);
      setSelectedStationData(stationData);
    } catch (error) {
      toast.error(UI_TEXT.ERRORS.FAILED_FETCH_STATION);
    }
  };

  const getStationsForMapData = useCallback(
    async (
      size: number,
      southwestLat: number,
      southwestLng: number,
      northeastLat: number,
      northeastLng: number,
      cancellationSignal: AbortSignal,
      fetchId: number
    ): Promise<void> => {
      setIsFetchingStations(true);
      try {
        const data = await getStationsForMap({
          size,
          page: CONFIG.API.DEFAULT_PAGE,
          southwestLat,
          southwestLng,
          northeastLat,
          northeastLng,
          cancellationSignal,
        });

        if (
          !cancellationSignal.aborted &&
          activeFetchIdRef.current === fetchId
        ) {
          startTransition(() => {
            setStations(data);
          });
        }
      } catch (error: any) {
        if (error.name !== ErrorType.ABORT) {
          throw error;
        }
      } finally {
        if (activeFetchIdRef.current === fetchId) {
          setIsFetchingStations(false);
        }
      }
    },
    []
  );

  const computeClusters = useCallback(() => {
    if (!map || !superclusterRef.current) {
      setClusters([]);
      return;
    }

    const bounds = map.getBounds();
    if (!bounds) {
      setClusters([]);
      return;
    }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const currentZoom = map.getZoom() || 0;

    const bbox: [number, number, number, number] = [
      sw.lng(),
      sw.lat(),
      ne.lng(),
      ne.lat(),
    ];

    const computedClusters = superclusterRef.current.getClusters(
      bbox,
      Math.floor(currentZoom)
    );
    setClusters(computedClusters);
  }, [map]);

  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (fetchControllerRef.current) {
        fetchControllerRef.current.abort();
        fetchControllerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (stations.length === 0) {
      superclusterRef.current = null;
      setClusters([]);
      return;
    }

    const points = stationsToGeoJSON(stations);
    const supercluster = new Supercluster({
      radius,
      maxZoom: CONFIG.MARKER_CLUSTERER.MAX_ZOOM,
      minZoom: 0,
      minPoints: CONFIG.MARKER_CLUSTERER.MINIMUM_CLUSTER_SIZE,
    });

    supercluster.load(points);
    superclusterRef.current = supercluster;

    if (map) {
      computeClusters();
    }
  }, [stations, map, computeClusters, radius]);

  useEffect(() => {
    if (!config.googleMapsApiKey) {
      setMapLoadError(UI_TEXT.ERRORS.MAP_API_KEY_NOT_CONFIGURED);
      console.error(
        "Google Maps API key is missing. Please set NEXT_PUBLIC_GOOGLE_MAPS_API_KEY environment variable."
      );
      return;
    }

    const handleScriptError = (event: ErrorEvent) => {
      if (
        event.message?.includes("Google Maps") ||
        event.message?.includes("maps.googleapis.com") ||
        event.filename?.includes("maps.googleapis.com")
      ) {
        console.error("Google Maps script error:", event);
        setMapLoadError(UI_TEXT.ERRORS.MAP_LOAD_FAILED);
      }
    };

    const timeoutId = setTimeout(() => {
      if (!map && config.googleMapsApiKey) {
        console.warn("Google Maps did not load within timeout period");
      }
    }, 10000);

    window.addEventListener("error", handleScriptError);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("error", handleScriptError);
    };
  }, [map]);

  const fetchControllerRef = useRef<Nullable<AbortController>>(null);
  const activeFetchIdRef = useRef<number>(0);
  const debounceTimeoutRef =
    useRef<Nullable<ReturnType<typeof setTimeout>>>(null);
  const isFirstLoadRef = useRef(true);

  const fetchStationsForBounds = useCallback(
    (bounds: IMapBounds) => {
      const paddedBounds = addPaddingToBounds(
        bounds,
        GENERAL.BOUNDS_PADDING_PERCENT
      );

      if (
        !cachedPaddedBounds ||
        isBoundsOutsideCached(bounds, cachedPaddedBounds)
      ) {
        if (fetchControllerRef.current) {
          fetchControllerRef.current.abort();
          fetchControllerRef.current = null;
        }

        const controller = new AbortController();
        fetchControllerRef.current = controller;

        activeFetchIdRef.current += 1;
        const currentFetchId = activeFetchIdRef.current;

        const currentController = controller;

        getStationsForMapData(
          GENERAL.STATION_FETCH_SIZE,
          paddedBounds.southwest.lat,
          paddedBounds.southwest.lng,
          paddedBounds.northeast.lat,
          paddedBounds.northeast.lng,
          controller.signal,
          currentFetchId
        )
          .then(() => {
            if (
              fetchControllerRef.current === currentController &&
              !currentController.signal.aborted
            ) {
              setCachedPaddedBounds(paddedBounds);
              fetchControllerRef.current = null;
            }
          })
          .catch((err) => {
            if (fetchControllerRef.current === currentController) {
              if (err.name !== ErrorType.ABORT) {
                toast.error(UI_TEXT.ERRORS.FAILED_FETCH_STATIONS);
              }
              fetchControllerRef.current = null;
            }
          });
      }

      computeClusters();
    },
    [cachedPaddedBounds, computeClusters, getStationsForMapData]
  );

  const handleMapIdle = useCallback(() => {
    if (!map) return;

    if (isAnimatingRef.current) {
      return;
    }

    const bounds = map.getBounds();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();

    const currentBounds: IMapBounds = {
      southwest: { lat: sw.lat(), lng: sw.lng() },
      northeast: { lat: ne.lat(), lng: ne.lng() },
    };

    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
      fetchStationsForBounds(currentBounds);
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (!isAnimatingRef.current) {
        fetchStationsForBounds(currentBounds);
      }
      debounceTimeoutRef.current = null;
    }, GENERAL.MAP_DEBOUNCE_TIMEOUT);
  }, [map, fetchStationsForBounds]);

  const handleMapCenterChanged = () => {
    const center = map?.getCenter() as google.maps.LatLng;
    const lat = center?.lat();
    const lng = center?.lng();
    if (lat && lng) {
      safeSetMapCenter({ lat, lng });
    }
    isDraggingRef.current = false;
  };

  const handleMapDragStart = () => {
    isDraggingRef.current = true;
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  };

  const handleMapClick = () => {
    setIsSearchFocused(false);
    setTimeout(() => {
      if (!isDraggingRef.current) {
        setSelectedStationData(null);
        setSelectedMarker(null);
      }
    }, 10);
  };

  const safeSetMapCenter = useCallback(
    (center: Nullable<IMapLatLngFlexible>, zoom?: number) => {
      if (!center || !map) return false;

      const numLat =
        typeof center.lat === "string" ? parseFloat(center.lat) : center.lat;
      const numLng =
        typeof center.lng === "string" ? parseFloat(center.lng) : center.lng;

      if (
        numLat != null &&
        numLng != null &&
        isFinite(numLat) &&
        isFinite(numLng)
      ) {
        const newCenter = { lat: numLat, lng: numLng };

        isAnimatingRef.current = true;

        if (zoom !== undefined) {
          const currentZoom = map.getZoom() || zoom;
          if (Math.abs(currentZoom - zoom) > 1) {
            map.setZoom(zoom);
            setTimeout(() => {
              map.panTo(newCenter);
            }, 300);
          } else {
            map.setZoom(zoom);
            map.panTo(newCenter);
          }
        } else {
          map.panTo(newCenter);
        }

        const updateState = () => {
          setMapCenter(newCenter);
          isAnimatingRef.current = false;
        };

        const idleListener = map.addListener("idle", () => {
          updateState();
          google.maps.event.removeListener(idleListener);
        });

        setTimeout(() => {
          if (isAnimatingRef.current) {
            updateState();
            google.maps.event.removeListener(idleListener);
          }
        }, GENERAL.ANIMATION_TIMEOUT);

        return true;
      }
      return false;
    },
    [map]
  );

  const handleClusterClick = useCallback(
    (cluster: SuperClusterType) => {
      if (!map || !superclusterRef.current) return;

      const expansionZoom = Math.min(
        superclusterRef.current.getClusterExpansionZoom(cluster.id as number),
        maxZoom
      );

      const [lng, lat] = cluster.geometry.coordinates;
      safeSetMapCenter({ lat, lng }, expansionZoom);
    },
    [map, safeSetMapCenter]
  );

  useEffect(() => {
    if (window.innerWidth < WINDOW_BREAKPOINTS.phone) {
      setIconSize(GENERAL.IMAGE_SIZES.MOBILE_ICON);
    }

    pageViewAnalitics(PAGE_TABS.map);

    if (typeof window !== "undefined" && window.innerWidth < tab_max_width) {
      setMinZoom(mob_min_zoom);
    }
  }, []);

  useEffect(() => {
    if (map) {
      const timeoutId = setTimeout(() => {
        handleMapIdle();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [map, handleMapIdle]);

  useEffect(() => {
    if (map && superclusterRef.current) {
      computeClusters();
    }
  }, [map, stations, computeClusters]);

  useEffect(() => {
    if (selectedStationData?.name) {
      setSearch(selectedStationData.name);
      if (selectedMarker?.latitude && selectedMarker?.longitude) {
        safeSetMapCenter({
          lat: selectedMarker.latitude,
          lng: selectedMarker.longitude,
        });
      }
    }
  }, [selectedStationData, selectedMarker, safeSetMapCenter]);

  useEffect(() => {
    if (contextQrPopup) {
      setQrPopup(true);
      if (socialType === "app-store" || socialType === APP_STORE) {
        setQrOrigin("app-store");
      } else {
        setQrOrigin("google-play");
      }
    } else {
      setQrPopup(false);
    }
  }, [contextQrPopup, socialType]);

  const validMapCenter =
    mapCenter.lat != null &&
    mapCenter.lng != null &&
    isFinite(mapCenter.lat) &&
    isFinite(mapCenter.lng)
      ? mapCenter
      : initialCenter;

  return (
    <div className={style.mapContainer}>
      {downloadPopup && <DownloadPopup onClose={handleCloseDownloadPopup} />}
      {qrPopup && <QrPopup onClose={handleCloseQrPopup} origin={qrOrigin} />}
      {(isLoading || isFetchingStations) && (
        <div className={style.loadingContainer}>
          <Spin
            indicator={<LoadingOutlined className={style.loadingIcon} spin />}
          />
        </div>
      )}
      {config.googleMapsApiKey && !mapLoadError ? (
        <LoadScriptNext googleMapsApiKey={config.googleMapsApiKey}>
          <GoogleMap
            mapContainerStyle={containerStyle}
            onLoad={(m) => {
              setMap(m);
              setMapLoadError(null);
            }}
            center={validMapCenter}
            zoom={initialZoom}
            options={{
              maxZoom: maxZoom,
              minZoom: desc_min_zoom,
              mapTypeControl: false,
              restriction: {
                latLngBounds,
              },
              fullscreenControl: false,
              cameraControl: false,
            }}
            onDragStart={handleMapDragStart}
            onDragEnd={handleMapCenterChanged}
            onClick={handleMapClick}
            onZoomChanged={() => {
              const radius = getClusterGridSize(map?.getZoom() || 0);
              setRadius(radius);

              if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
                debounceTimeoutRef.current = null;
              }
              const center = map?.getCenter();
              if (
                center &&
                (center.lat() !== mapCenter.lat ||
                  center.lng() !== mapCenter.lng) &&
                isFinite(center.lat()) &&
                isFinite(center.lng())
              ) {
                handleMapCenterChanged();
              }
              computeClusters();
            }}
            onIdle={handleMapIdle}
          >
            {clusters.map((cluster) => {
              const [lng, lat] = cluster.geometry.coordinates;
              const isCluster = cluster.properties?.cluster;

              if (isCluster) {
                const pointCount = cluster.properties.point_count;
                return (
                  <Marker
                    key={`cluster-${cluster.id}`}
                    position={{ lat, lng }}
                    icon={{
                      url: "/mapIcons/Clusterer.svg",
                      scaledSize: new google.maps.Size(iconSize, iconSize),
                    }}
                    onClick={() => handleClusterClick(cluster)}
                    label={
                      {
                        text: pointCount.toString(),
                        color: CONFIG.MARKER_CLUSTERER.TEXT_COLOR,
                        className: style.clusterClass,
                      } as google.maps.MarkerLabel
                    }
                  />
                );
              } else {
                const stationId = cluster.properties.stationId;
                const stationType = cluster.properties.type;
                const station = stations.find((s) => s.id === stationId);

                if (!station) return null;

                return (
                  <Marker
                    key={`station-${stationId}`}
                    position={{ lat, lng }}
                    icon={{
                      url: getMarkerIcon(
                        stationType,
                        stationId,
                        selectedMarker?.id || null,
                        selectedStationData?.id || null
                      ),
                      scaledSize: new google.maps.Size(iconSize, iconSize),
                    }}
                    onClick={() => handleSelectStation(station)}
                  />
                );
              }
            })}
            {userLocation && (
              <Marker
                position={userLocation}
                icon={{
                  url: "/mapIcons/CurrentLocation.svg",
                  scaledSize: new google.maps.Size(iconSize, iconSize),
                }}
              />
            )}
            {selectedStationData && (
              <StationDetails
                stationData={selectedStationData}
                onOpenDownloadPopup={handleOpenDownloadPopupSimple}
                onClose={() => {
                  setSelectedStationData(null);
                  setSelectedMarker(null);
                }}
              />
            )}
            <SearchPanel
              clear={() => {
                setSelectedMarker(null);
                setSelectedStationData(null);
              }}
              onSelectLocation={handleSelectStation}
              selectedMarker={selectedMarker}
              setSelectedMarker={setSelectedMarker}
              search={search}
              setSearch={setSearch}
              clearSelectedStationData={() => setSelectedStationData(null)}
              isFocused={isSearchFocused}
              setIsFocused={setIsSearchFocused}
            />
            <Control
              setMapCenter={safeSetMapCenter}
              eventCalled={() => setSelectedMarker(null)}
              setUserLocation={setUserLocation}
              map={map}
              minZoom={minZoom}
              onOpenDownloadPopup={handleOpenDownloadPopupSimple}
              isTrackingLocation={isTrackingLocation}
              startTrackingLocation={startTrackingLocation}
              stopTrackingLocation={stopTrackingLocation}
            />
          </GoogleMap>
        </LoadScriptNext>
      ) : null}
      {mapLoadError && (
        <div className={style.mapErrorContainer}>
          <p className={style.mapErrorTitle}>{mapLoadError}</p>
          <p className={style.mapErrorDescription}>
            {UI_TEXT.ERRORS.MAP_ERROR_DESCRIPTION}
            <br />
            1. {UI_TEXT.ERRORS.MAP_ERROR_API_KEY}
            <br />
            2. {UI_TEXT.ERRORS.MAP_ERROR_DOMAIN}
            <br />
            3. {UI_TEXT.ERRORS.MAP_ERROR_APIS_ENABLED}
          </p>
        </div>
      )}
    </div>
  );
};

export default AviationMap;
